/**
 * Privacy Policy page — Danish GDPR-required privacy information.
 * Matches web frontend `privatlivspolitik/page.tsx`.
 */
import { ScrollView, View, Text, Pressable, StyleSheet, Linking } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useRouter } from 'expo-router'
import { ArrowLeft } from 'lucide-react-native'
import { colors, spacing, borderRadius, typography } from '../src/theme'

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <View style={s.section}>
    <Text style={s.sectionTitle}>{title}</Text>
    {children}
  </View>
)

const Bullet = ({ bold, text }: { bold?: string; text: string }) => (
  <View style={s.bulletRow}>
    <Text style={s.bullet}>{'\u2022'}</Text>
    <Text style={s.bodyText}>
      {bold && <Text style={s.bold}>{bold} </Text>}
      {text}
    </Text>
  </View>
)

export default function PrivatlivspolitikScreen() {
  const router = useRouter()

  return (
    <SafeAreaView style={s.container} edges={['top']}>
      <View style={s.header}>
        <Pressable onPress={() => router.back()} hitSlop={12}>
          <ArrowLeft size={20} color={colors.primary} />
        </Pressable>
        <Text style={s.headerTitle}>Privatlivspolitik</Text>
        <View style={{ width: 20 }} />
      </View>

      <ScrollView contentContainerStyle={s.content}>
        <Text style={s.lastUpdated}>Sidst opdateret: 27. februar 2026</Text>

        <Section title="1. Dataansvarlig">
          <Text style={s.bodyText}>
            ForbrugerAgenten er dataansvarlig for behandling af dine personoplysninger.
            Kontakt os på{' '}
            <Text onPress={() => Linking.openURL('mailto:privacy@forbrugeragenten.dk')} style={{ color: colors.primary, textDecorationLine: 'underline' }}>
              privacy@forbrugeragenten.dk
            </Text>{' '}
            ved spørgsmål om dine data.
          </Text>
        </Section>

        <Section title="2. Hvilke data indsamler vi">
          <Text style={s.bodyText}>Vi indsamler følgende personoplysninger:</Text>
          <Bullet bold="Identifikation:" text="Navn, CPR-nummer (via MitID), email" />
          <Bullet bold="Kontaktinfo:" text="Adresse, postnummer, by, telefonnummer" />
          <Bullet bold="Forbrugsdata:" text="Nuværende selskaber, forbrug (kWh, GB), abonnementer" />
          <Bullet bold="Kommunikation:" text="Beskeder mellem dig og selskaber via agent email" />
          <Bullet bold="Tilbud:" text="Modtagne tilbud og dine valg" />
        </Section>

        <Section title="3. Formål med behandling">
          <Bullet text="Verificere din identitet via MitID" />
          <Bullet text="Indhente tilbud fra selskaber på dine vegne" />
          <Bullet text="Præsentere og sammenligne tilbud" />
          <Bullet text="Give personlig AI-rådgivning om dine aftaler" />
          <Bullet text="Kommunikere med selskaber via din agent email" />
        </Section>

        <Section title="4. Retsgrundlag">
          <Text style={s.bodyText}>
            Vi behandler dine data på baggrund af dit samtykke (GDPR art. 6, stk. 1, litra a)
            og for at opfylde aftalen om at levere tjenesten (GDPR art. 6, stk. 1, litra b).
            CPR-nummer behandles med dit eksplicitte samtykke til identitetsverifikation.
          </Text>
        </Section>

        <Section title="5. Deling af data">
          <Text style={s.bodyText}>Vi deler kun dine data med:</Text>
          <Bullet bold="Selskaber:" text="Når du starter en jagt, deler vi adresse og forbrugsdata (IKKE nuværende pris eller selskab) med potentielle leverandører" />
          <Bullet bold="MitID/Criipto:" text="Til identitetsverifikation" />
          <Bullet bold="Hosting:" text="Data opbevares hos Railway (EU-baseret infrastruktur)" />
          <Bullet bold="Analyse:" text="PostHog (produktanalyse og sessionsoptagelser — anonymiserede data)" />
          <Text style={[s.bodyText, { marginTop: 8 }]}>Vi sælger aldrig dine data til tredjeparter.</Text>
        </Section>

        <Section title="6. Opbevaring">
          <Text style={s.bodyText}>
            Dine data opbevares så længe du har en aktiv konto. Ved kontosletning fjernes
            alle persondata permanent inden for 30 dage. Anonymiserede, aggregerede data
            kan bevares til statistiske formål.
          </Text>
        </Section>

        <Section title="7. Dine rettigheder">
          <Text style={s.bodyText}>Du har ret til:</Text>
          <Bullet bold="Indsigt:" text="Se hvilke data vi har om dig (via Indstillinger → Eksporter data)" />
          <Bullet bold="Berigtigelse:" text="Rette forkerte oplysninger (via Indstillinger)" />
          <Bullet bold="Sletning:" text="Slette din konto og alle data (via Indstillinger)" />
          <Bullet bold="Dataportabilitet:" text="Eksportere dine data i maskinlæsbart format" />
          <Bullet bold="Tilbagetrækning af samtykke:" text="Du kan til enhver tid trække dit samtykke tilbage" />
        </Section>

        <Section title="8. Sikkerhed">
          <Text style={s.bodyText}>
            Vi bruger krypteret forbindelse (HTTPS), sikker autentificering via MitID,
            og adgangskontrol på alle data. CPR-numre opbevares krypteret. Alle API-kald
            er autentificeret med JWT-tokens.
          </Text>
        </Section>

        <Section title="9. Cookies og analyse">
          <Text style={s.bodyText}>
            Vi bruger nødvendige cookies til login (JWT-token i localStorage).
          </Text>
          <Text style={[s.bodyText, { marginTop: 8 }]}>
            Derudover bruger vi <Text style={s.bold}>PostHog</Text> til produktanalyse og sessionsoptagelser,
            så vi kan forbedre brugeroplevelsen. PostHog indsamler anonymiserede sidevisninger,
            klikhændelser og sessionsoptagelser. Alle formularfelter maskeres automatisk i optagelser.
          </Text>
          <Text style={[s.bodyText, { marginTop: 8 }]}>
            Du kan fravælge analyse-tracking ved at kontakte os på{' '}
            <Text onPress={() => Linking.openURL('mailto:privacy@forbrugeragenten.dk')} style={{ color: colors.primary, textDecorationLine: 'underline' }}>
              privacy@forbrugeragenten.dk
            </Text>.
          </Text>
        </Section>

        <Section title="10. Klage">
          <Text style={s.bodyText}>
            Du kan klage over vores behandling af dine data til Datatilsynet:
          </Text>
          <Pressable onPress={() => Linking.openURL('https://www.datatilsynet.dk')}>
            <Text style={s.link}>datatilsynet.dk</Text>
          </Pressable>
        </Section>

        <View style={{ height: 40 }} />
      </ScrollView>
    </SafeAreaView>
  )
}

const s = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.surface },
  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    backgroundColor: colors.background, borderBottomWidth: 1, borderBottomColor: colors.border,
  },
  headerTitle: { ...typography.bodyBold, color: colors.text.primary },
  content: { padding: spacing.md },
  lastUpdated: { fontSize: 12, color: colors.text.muted, marginBottom: spacing.md },
  section: { marginBottom: spacing.lg },
  sectionTitle: { fontSize: 16, fontWeight: '600', color: colors.text.primary, marginBottom: 8 },
  bodyText: { fontSize: 14, color: '#374151', lineHeight: 22 },
  bold: { fontWeight: '600' },
  bulletRow: { flexDirection: 'row', gap: 8, marginTop: 4, paddingRight: spacing.md },
  bullet: { fontSize: 14, color: '#374151', lineHeight: 22 },
  link: { fontSize: 14, color: colors.primary, textDecorationLine: 'underline', marginTop: 4 },
})
